<template>
  <v-card flat class="bar-chart-card">
    <h3 class="card-title">{{ settings.title }}</h3>

    <v-row class="filters" dense v-if="resolvedFilters.length">
      <v-col v-for="f in resolvedFilters" :key="f.key" cols="12" sm="4">
        <v-autocomplete
          v-if="f.multiple"
          outlined
          dense
          hide-details
          multiple
          small-chips
          deletable-chips
          class="roundish"
          :label="f.label"
          :items="optionsFor(f)"
          :loading="!!filterOptionsLoading[f.key]"
          v-model="filterValues[f.key]"
          clearable
        />
        <v-select
          v-else
          outlined
          dense
          hide-details
          class="roundish"
          :label="f.label"
          :items="optionsFor(f)"
          :loading="!!filterOptionsLoading[f.key]"
          v-model="filterValues[f.key]"
          clearable
        />
      </v-col>
    </v-row>

    <div class="chart-header">
      <div class="col-label sortable" @click="toggleSort('label')">
        {{ settings.categoryLabel || "CATEGORY" }}
        <v-icon v-if="sortField === 'label'" x-small>{{ sortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
      </div>
      <div class="col-count-label sortable" @click="toggleSort('value')">
        {{ settings.valueLabel || "VALUE" }}
        <v-icon v-if="sortField === 'value'" x-small>{{ sortAsc ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
      </div>
    </div>

    <div class="bars">
      <div class="bar-row" v-for="row in sortedRows" :key="row.label">
        <div class="label">{{ row.label }}</div>
        <div class="count">{{ Number(row.value).toLocaleString() }}</div>
        <div class="bar-wrap">
          <div
            class="bar"
            :style="{
              width: (Number(row.value) / maxValue) * 100 + '%',
              background: settings.barColor,
            }"
          ></div>
        </div>
        <v-btn icon small class="row-action">
          <v-icon small>more_horiz</v-icon>
        </v-btn>
      </div>
      <div v-if="!resolvedRows.length && !isLoading" class="empty">No data.</div>
      <div v-if="isLoading" class="empty">Loading…</div>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";

/**
 * Generic horizontal bar chart with filter dropdowns.
 *
 * Dual-mode:
 *  - Standalone: pass `rows` (array of { label, value }) and optionally
 *    `filters` (array of { key, label, options: string[], default? }).
 *  - Landing-config (controlType: 'BarChart'):
 *      definition: {
 *        controlType: 'BarChart',
 *        title, barColor, categoryLabel, valueLabel,
 *        dataSource: 'static' | 'api',
 *        // when dataSource === 'api':
 *        apiEndpoint, apiMethod, apiParams: [{ name, value }],
 *        rowMapping: { labelField, valueField },
 *        // when dataSource === 'static':
 *        rows: [{ label, value }],
 *        // Filters — shown as dropdowns above the chart.
 *        // In api mode each filter's value is merged into the request
 *        // under its `key`, triggering a refetch on change.
 *        filters: [{ key, label, options: string[], default? }]
 *      }
 *
 * API response contract:
 *  - Either an array of rows, or { Status: 'OK', Data: [rows] }
 *  - Each row must contain labelField and valueField as configured
 *
 * Emits:
 *  - sessionExpired — on 401
 */
export default {
  name: "cBarChart",
  props: {
    // landing-config mode
    definition: { type: Object, default: null },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
    // standalone mode
    title: { type: String, default: "Bar Chart" },
    rows: { type: Array, default: null },
    filters: { type: Array, default: null },
    barColor: { type: String, default: "#2B9DBF" },
  },
  data() {
    return {
      filterValues: {},
      apiRows: [],
      isLoading: false,
      sortField: null,
      sortAsc: true,
      // Keyed by filter.key — holds the last-fetched dynamic option list.
      filterOptions: {},
      filterOptionsLoading: {},
      // Keyed by filter.key — options extracted from the API response via
      // allItemsField, persisted so they don't shrink when data is filtered.
      persistedFilterOptions: {},
    };
  },
  computed: {
    settings() {
      const d = this.definition || {};
      return {
        title: d.title ?? this.title,
        rows: d.rows ?? null,
        filters: d.filters ?? null,
        barColor: d.barColor ?? this.barColor,
        categoryLabel: d.categoryLabel ?? "CATEGORY",
        valueLabel: d.valueLabel ?? "VALUE",
        dataSource: d.dataSource ?? "static",
        apiEndpoint: d.apiEndpoint ?? null,
        apiMethod: d.apiMethod ?? "POST",
        apiParams: d.apiParams ?? [],
        rowMapping: d.rowMapping ?? { labelField: "label", valueField: "value" },
      };
    },
    resolvedFilters() {
      if (this.settings.filters && this.settings.filters.length)
        return this.settings.filters;
      if (this.filters && this.filters.length) return this.filters;
      return [];
    },
    resolvedRows() {
      if (this.settings.dataSource === "api") return this.apiRows;
      if (this.settings.rows && this.settings.rows.length)
        return this.settings.rows;
      if (this.rows && this.rows.length) return this.rows;
      return [];
    },
    sortedRows() {
      const rows = [...this.resolvedRows];
      if (!this.sortField) return rows;
      const asc = this.sortAsc ? 1 : -1;
      if (this.sortField === "value") {
        rows.sort((a, b) => ((Number(a.value) || 0) - (Number(b.value) || 0)) * asc);
      } else {
        rows.sort((a, b) => String(a.label).localeCompare(String(b.label)) * asc);
      }
      return rows;
    },
    maxValue() {
      return Math.max(
        1,
        ...this.resolvedRows.map((r) => Number(r.value) || 0)
      );
    },
    apiFetchKey() {
      return JSON.stringify({
        ds: this.settings.dataSource,
        ep: this.settings.apiEndpoint,
        m: this.settings.apiMethod,
        p: this.settings.apiParams,
        f: this.filterValues,
      });
    },
  },
  watch: {
    resolvedFilters: {
      immediate: true,
      handler(fs) {
        const next = { ...this.filterValues };
        fs.forEach((f) => {
          if (next[f.key] === undefined) next[f.key] = f.default ?? (f.multiple ? [] : null);
        });
        this.filterValues = next;
        // Kick off option fetches for any filter with an endpoint
        fs.forEach((f) => {
          if (f.optionsEndpoint) this.fetchFilterOptions(f);
        });
      },
    },
    apiFetchKey: {
      immediate: true,
      handler() {
        if (this.settings.dataSource === "api" && this.settings.apiEndpoint) {
          this.fetchData();
        } else {
          this.apiRows = [];
        }
        // Cascading: re-fetch dynamic filter options so filter B's list can
        // depend on filter A's value (backend reads the current filter values
        // from the request and returns a filtered list).
        this.resolvedFilters.forEach((f) => {
          if (f.optionsEndpoint) this.fetchFilterOptions(f);
        });
      },
    },
  },
  methods: {
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortField = field;
        this.sortAsc = true;
      }
    },
    buildParamsObject() {
      const out = {};
      (this.settings.apiParams || []).forEach((p) => {
        if (p && p.name) out[p.name] = p.value;
      });
      // filter values are merged in — each filter's `key` becomes a param.
      // Empty arrays are omitted (meaning "all" / no filter).
      Object.keys(this.filterValues || {}).forEach((k) => {
        const v = this.filterValues[k];
        if (Array.isArray(v)) {
          if (v.length) out[k] = v;
          return;
        }
        if (v !== null && v !== undefined && v !== "") out[k] = v;
      });
      return out;
    },
    fetchData() {
      if (this.editMode) return;
      const endpoint = this.settings.apiEndpoint;
      if (!endpoint) return;

      const method = (this.settings.apiMethod || "POST").toUpperCase();
      const params = this.buildParamsObject();
      const request =
        method === "GET"
          ? axios.get(endpoint, { params })
          : axios.post(endpoint, params);

      this.isLoading = true;
      request
        .then((resp) => {
          const data = resp.data;
          let raw;
          if (Array.isArray(data)) {
            raw = data;
          } else if (data && data.Status === "OK") {
            // Data can be a plain array or { rows, ...metadata }
            if (Array.isArray(data.Data)) {
              raw = data.Data;
            } else if (data.Data && Array.isArray(data.Data.rows)) {
              raw = data.Data.rows;
              // Persist allItemsField options so they survive filter changes
              this.resolvedFilters.forEach((f) => {
                if (f.allItemsField && Array.isArray(data.Data[f.allItemsField])) {
                  this.$set(
                    this.persistedFilterOptions,
                    f.key,
                    data.Data[f.allItemsField].map((item) =>
                      typeof item === "string" || typeof item === "number"
                        ? { text: String(item), value: item }
                        : item
                    )
                  );
                }
              });
            }
          }
          this.apiRows = this.mapToRows(raw);
          this.isLoading = false;
        })
        .catch((err) => {
          this.isLoading = false;
          if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            console.error("BarChart fetch failed", err);
          }
        });
    },
    mapToRows(raw) {
      if (!Array.isArray(raw)) return [];
      const m = this.settings.rowMapping || {};
      const labelField = m.labelField || "label";
      const valueField = m.valueField || "value";
      return raw.map((r) => ({
        label: r[labelField],
        value: Number(r[valueField]) || 0,
      }));
    },
    /**
     * Returns the option list for a filter.
     * Priority: persisted allItemsField > dynamic endpoint > static options.
     */
    optionsFor(f) {
      if (!f) return [];
      if (f.allItemsField && this.persistedFilterOptions[f.key]?.length)
        return this.persistedFilterOptions[f.key];
      if (f.optionsEndpoint && this.filterOptions[f.key])
        return this.filterOptions[f.key];
      return f.options || [];
    },
    /**
     * Fetch options for a single filter from its `optionsEndpoint`.
     * Sends the current filterValues as params so the backend can
     * narrow results (e.g. Job Family list scoped by Job Family Group).
     */
    fetchFilterOptions(f) {
      if (this.editMode) return;
      if (!f || !f.optionsEndpoint) return;

      // Re-use the main filter values + any static optionsParams as body/params.
      const body = {};
      (f.optionsParams || []).forEach((p) => {
        if (p && p.name) body[p.name] = p.value;
      });
      Object.keys(this.filterValues || {}).forEach((k) => {
        if (k === f.key) return; // don't send a filter's own value as a param to its own lookup
        const v = this.filterValues[k];
        if (v !== null && v !== undefined && v !== "") body[k] = v;
      });

      const method = (f.optionsMethod || "GET").toUpperCase();
      const request =
        method === "POST"
          ? axios.post(f.optionsEndpoint, body)
          : axios.get(f.optionsEndpoint, { params: body });

      this.$set(this.filterOptionsLoading, f.key, true);
      request
        .then((resp) => {
          const raw = Array.isArray(resp.data)
            ? resp.data
            : resp.data && resp.data.Status === "OK"
            ? resp.data.Data
            : null;
          this.$set(this.filterOptions, f.key, this.mapToOptions(raw, f));
          this.$set(this.filterOptionsLoading, f.key, false);
        })
        .catch((err) => {
          this.$set(this.filterOptionsLoading, f.key, false);
          if (err.response && err.response.status === 401) {
            this.$emit("sessionExpired", err);
          } else {
            console.error(`BarChart filter options fetch failed for "${f.key}"`, err);
          }
        });
    },
    mapToOptions(raw, f) {
      if (!Array.isArray(raw)) return [];
      const m = f.optionsMapping || {};
      const labelField = m.labelField || "label";
      const valueField = m.valueField || "value";
      return raw.map((item) => {
        // string items → use as both label and value
        if (typeof item === "string" || typeof item === "number")
          return { text: String(item), value: item };
        // object items → map
        return {
          text: item[labelField] ?? item[valueField] ?? "",
          value: item[valueField] ?? item[labelField] ?? "",
        };
      });
    },
  },
};
</script>

<style scoped lang="scss">
.bar-chart-card {
  font-family: 'DM Sans', sans-serif !important;
  ::v-deep *:not(.v-icon):not(.v-icon *) { font-family: inherit !important; }
  h1, h2, ::v-deep h1, ::v-deep h2 {
    font-family: 'DM Serif Display', serif !important;
  }

  border-radius: 16px !important;
  padding: 20px 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04) !important;
  border: 1px solid #eef0f4;

  .card-title {
    font-size: 18px;
    font-weight: 700;
    margin: 0 0 16px 0;
    color: #1f2937;
  }

  .filters {
    margin-bottom: 12px;
  }

  .chart-header {
    display: grid;
    grid-template-columns: 140px 1fr 40px;
    align-items: center;
    padding: 10px 12px;
    font-size: 11px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #6b7280;
    background: #EEEEEE;
    border-radius: 8px 8px 0 0;

    .sortable {
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
      gap: 2px;
      &:hover { color: #374151; }
    }

    .col-count-label {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .bars {
    .bar-row {
      display: grid;
      grid-template-columns: 140px 60px 1fr 40px;
      align-items: center;
      padding: 10px 12px;
      font-size: 14px;

      &:nth-child(even) { background: #F7F9FB; }

      .label { color: #374151; }
      .count { color: #1f2937; font-weight: 600; }

      .bar-wrap {
        background: transparent;
        height: 18px;
        display: flex;
        align-items: center;

        .bar {
          height: 100%;
          background: #2B9DBF;
          border-radius: 3px;
          min-width: 2px;
        }
      }

      .row-action {
        justify-self: end;
      }
    }

    .empty {
      padding: 24px;
      text-align: center;
      color: #6b7280;
    }
  }
}
</style>
